import { useCallback } from 'react';
import { nanoid } from 'nanoid';

import { SettingsGroup } from 'approot/activity-builder/settings/settings.style';
import {
  SettingProps,
  MAX_SEQUENCE_ITEMS,
} from './interactive-response.constants';
import {
  ACTIVITIES_RESPONSE_VARIANT_IMAGE,
  ACTIVITIES_RESPONSE_VARIANT_TEXT,
  ACTIVITIES_RESPONSE_VARIANT_TEXT_IMAGE,
} from 'global/constants';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import { Box, BoxList } from 'approot/shared/design-system/box/box.style';
import { useInteractiveResponseUpdate } from './interactive-response-settings.hooks';
import InteractiveResponseItemsText from './interactive-response-items-text';
import { getReorderSort } from 'approot/shared/sortable/sortable.utils';
import { DIRECTION_LEFT } from 'webclient.constants';
import { FIT } from 'approot/activity-builder/components/image/image-crop.constants';
import { COLOUR_WHITE } from '../../interactive-response-shared.constants';
import InteractiveResponseItemImage from './interactive-response-item-image';
import { getVariant } from '../../shared/interactive-response-shared.utils';

const InteractiveResponseItems = (props: SettingProps) => {
  const { properties } = props;

  const { handleUpdate } = useInteractiveResponseUpdate(props);

  const variant = getVariant({
    variant: properties.interactiveResponse.variant,
  });

  // if any child does not have a variant, set the variant to the parent variant
  const items = properties.interactiveResponse.items.map(item => ({
    ...item,
    variant: item.variant || variant,
  }));

  const isAtMaxItems = items.length >= MAX_SEQUENCE_ITEMS;

  const createNewItem = useCallback(
    (itemVariant: string) => {
      handleUpdate(
        {
          ...properties,
          interactiveResponse: {
            ...properties.interactiveResponse,
            items: [
              ...items,
              {
                itemId: nanoid(),
                answerLabel: '',
                questionLabel: '',
                text: null,
                richtext: null,
                baseColour: COLOUR_WHITE.base,
                audio: null,
                sort: getReorderSort(items, items.length, DIRECTION_LEFT),
                cropData: null,
                sourceUrl: null,
                imageMode: FIT,
                imageUrls: [],
                variant: itemVariant,
                image: null,
                __typename: 'InteractiveResponseItem',
              },
            ],
          },
        },
        {
          isSaveImmediate: true,
        }
      );
    },
    [properties, items, handleUpdate]
  );

  const handleAddImageOnly = useCallback(() => {
    createNewItem(ACTIVITIES_RESPONSE_VARIANT_IMAGE);
  }, [createNewItem]);

  const handleAddTextOnly = useCallback(() => {
    createNewItem(ACTIVITIES_RESPONSE_VARIANT_TEXT);
  }, [createNewItem]);

  const handleAddImageAndText = useCallback(() => {
    createNewItem(ACTIVITIES_RESPONSE_VARIANT_TEXT_IMAGE);
  }, [createNewItem]);

  return (
    <>
      <SettingsGroup>
        <BoxList data-selector="items-list">
          {items.map((item, index) => {
            if (item.variant === ACTIVITIES_RESPONSE_VARIANT_TEXT) {
              return (
                <InteractiveResponseItemsText
                  key={item.itemId}
                  {...props}
                  item={item}
                  data-selector={`sequence-item-${index}`}
                />
              );
            } else if (
              item.variant === ACTIVITIES_RESPONSE_VARIANT_TEXT_IMAGE
            ) {
              return (
                <InteractiveResponseItemImage
                  key={item.itemId}
                  {...props}
                  item={item}
                  includeText={true}
                  data-selector={`sequence-item-${index}`}
                />
              );
            } else {
              return (
                <InteractiveResponseItemImage
                  key={item.itemId}
                  {...props}
                  item={item}
                  includeText={false}
                  data-selector={`sequence-item-${index}`}
                />
              );
            }
          })}
        </BoxList>
      </SettingsGroup>

      <Box $gap={8} $marginTop={12}>
        <SecondaryButton
          type="button"
          $isSmall
          disabled={isAtMaxItems}
          onClick={handleAddImageOnly}
          data-testid="btn-add-image-only"
          data-selector="btn-add-image-only"
        >
          Add Image Only
        </SecondaryButton>
        <SecondaryButton
          type="button"
          $isSmall
          disabled={isAtMaxItems}
          onClick={handleAddTextOnly}
          data-testid="btn-add-text-only"
          data-selector="btn-add-text-only"
        >
          Add Text Only
        </SecondaryButton>
        <SecondaryButton
          type="button"
          $isSmall
          disabled={isAtMaxItems}
          onClick={handleAddImageAndText}
          data-testid="btn-add-image-and-text"
          data-selector="btn-add-image-and-text"
        >
          Add Image and Text
        </SecondaryButton>
      </Box>
    </>
  );
};

export default InteractiveResponseItems;
